<template>
  <div
    class="flex-row flex-grow position-relative h-full"
    data-test="producer-timeline"
  >
    <div v-if="!isSocketConnected" class="disconnected-overlay" />

    <div class="playlist level-01dp">
      <div class="header panel-drag-handle">
        <div class="playlist-info">
          <span class="title mr-4">Playlist</span>
        </div>

        <div class="playlist-settings panel-drag-exclude">
          <button
            class="tb-btn tb-btn-contained medium-em sm mr-3"
            data-entity="producer-timeline-saveAsShow"
            :disabled="savingAsShow"
            @click="confirmSaveAsShow"
          >
            <span class="fas fa-save" />
            Save as Show
          </button>

          <dropdown
            position="bottom left"
            class="margin-right-sm dropdown-menu-w-a"
          >
            <template #trigger>
              <button
                class="tb-btn tb-btn-contained medium-em sm add-button"
                data-entity="producer-timeline-add"
              >
                <span class="add-button-label">Add</span>
                <span class="fas fa-chevron-down add-button-icon" />
              </button>
            </template>

            <template #dropdown-body>
              <button
                class="dropdown-list-item"
                data-entity="producer-timeline-addSegment"
                @click="addSegment"
              >
                <span class="dropdown-list-item-label">Add Segment</span>
              </button>

              <button
                class="dropdown-list-item"
                data-entity="producer-timeline-addGroup"
                @click="addGraphicsGroup"
              >
                <span class="dropdown-list-item-label">
                  Add Custom Scene
                </span>
              </button>

              <button
                class="dropdown-list-item"
                data-entity="producer-timeline-addMarker"
                @click="addMarker"
              >
                <span class="dropdown-list-item-label">Add Marker</span>
              </button>
            </template>
          </dropdown>

          <div class="vertical-divider"></div>

          <div class="playlist-resizer mx-4">
            <span class="fas fa-image mr-1" style="font-size: 0.8em;" />
            <input class="tb-range" v-model="zoom" type="range" min="1" max="5" step="1">
            <span class="fas fa-image ml-1" style="font-size: 1.2em;" />
          </div>

          <div class="vertical-divider"></div>

          <button
            id="timelineSettingsButton"
            title="Timeline settings"
            class="tb-btn tb-btn-link sm low-em ml-2"
            data-entity="producer-timeline-openSettings"
            @click="showSettingsModal = true"
          >
            <span class="fas fa-cog" />
          </button>
        </div>
      </div>

      <div class="body">
        <div v-if="!layouts.length" class="playlist-droparea">
          <div class="droparea">
            <div class="droparea-help-text">
              Drag + drop Social, Classic Graphics, or Custom Graphics here
            </div>
          </div>
        </div>

        <draggable
          v-model="layouts"
          @change="onChange"
          @start="startDrag"
          @end="endDrag"
          class="playlist-draggable draggable"
          :class="[{'empty': !this.layouts.length},'zoom-' + zoom]"
          v-bind="{
            group: { name: 'timeline', pull: true, put: ['timeline', 'segmentGroup'] },
          }"
          :animation="200"
          revert-on-spill="true"
          handle=".drag-handle"
          data-test="producer-timeline-droparea"
        >
          <component
            v-for="(item, index) in layouts"
            :key="item.timeline_id"
            :is="getType(item)"
            :data-timeline-id="item.timeline_id"
            :class="{ 'timeline-item-flash': flashedTimelineId === item.timeline_id }"
            :index="index"
            :item="item"
            :prev="index > 0 ? layouts[index - 1] : null"
            :next="index < layouts.length - 1 ? layouts[index + 1] : null"
            :zoom="zoom"
          />
        </draggable>
      </div>

      <div class="footer playlist-toolbar">
        <div class="playlist-actions left">
          <button
            :data-balloon="timelineMuted ? 'Unmute' : 'Mute'"
            data-balloon-pos="up-left"
            class="tb-btn tb-btn-text sm medium-em"
            :class="timelineMuted ? 'active' : ''"
            data-entity="producer-timeline-toggleMute"
            @click="toggleMute"
          >
            <span class="fas" :class="timelineMuted ? 'fa-volume-mute' : 'fa-volume-up'" />
          </button>

          <button
            :data-balloon="timelineLooped ? 'Disable Loop' : 'Enable Loop'"
            data-balloon-pos="up-left"
            class="tb-btn tb-btn-text sm medium-em"
            :class="timelineLooped ? 'active' : ''"
            data-entity="producer-timeline-toggleRepeat"
            @click="toggleLoop"
          >
            <span class="fas fa-repeat" />
          </button>
        </div>

        <div class="playlist-actions middle">
          <button
            class="tb-btn tb-btn-text sm medium-em"
            data-balloon="Go Back"
            data-balloon-pos="up"
            :disabled="!hasActiveItem"
            data-entity="producer-timeline-goBack"
            @click="advance('previous')"
          >
            <span class="fas fa-step-backward" />
          </button>

          <button
            class="tb-btn tb-btn-text sm medium-em"
            :class="{ 'active': playing }"
            :disabled="!hasActiveItem"
            data-balloon="Toggle Auto-Advance"
            data-balloon-pos="up"
            data-entity="producer-timeline-togglePlaying"
            @click="togglePlaying"
          >
            <span
              class="fas"
              :class="playing ? 'fa-pause' : 'fa-play'"
            />
          </button>

          <button
            class="tb-btn tb-btn-text sm medium-em"
            data-balloon="Go Forward"
            data-balloon-pos="up"
            :disabled="!hasActiveItem"
            data-entity="producer-timeline-goForward"
            @click="advance('next')"
          >
            <span class="fas fa-step-forward" />
          </button>
        </div>

        <div class="playlist-actions right">
          <template v-if="showDuration">
              <span v-if="duration" class="countdown mr-4">
              {{ toMinutesAndSeconds(countdown) }} / {{ toMinutesAndSeconds(duration) }}
            </span>
          </template>

          <button
            class="tb-btn tb-btn-text sm medium-em"
            :disabled="!layouts || !layouts.length"
            data-entity="producer-timeline-clearPlaylist"
            @click="confirm"
          >
            Clear
          </button>
        </div>

      </div>

      <alert
        v-if="showAlert"
        footerConfirm="true"
        @confirm="clearTimeline"
        @close="showAlert = false"
      >
        <h2>Are you sure?</h2>
        <p>This will clear out everything in the playlist.</p>
      </alert>

      <alert
        v-if="showSaveAsShowConfirm"
        footerConfirm="true"
        :confirm-label="saveAsShowConfirmLabel"
        :cancel-label="saveAsShowCancelLabel"
        :confirm-sub-label="saveAsShowNeedsSegments ? 'Editable in Show Builder' : undefined"
        :cancel-sub-label="saveAsShowNeedsSegments ? 'Back to the playlist' : undefined"
        @confirm="saveAsShow"
        @close="showSaveAsShowConfirm = false"
      >
        <template v-if="saveAsShowNeedsSegments">
          <h2>{{ unsegmentedItemCount === 1 ? "1 item isn't in a segment" : `${unsegmentedItemCount} items aren't in a segment` }}</h2>
          <p>Unlike playlists, shows require every item to be in a segment.</p>
        </template>

        <template v-else>
          <h2>Save this timeline as a Show?</h2>
          <p>A new Show will be generated from the current production and timeline.</p>
        </template>
      </alert>
    </div>

    <modal
      v-if="showSettingsModal"
      class="producer-settings-modal"
      @close="showSettingsModal = false"
    >
      <template #header>
        <div class="title">Production Options</div>
        <button
          class="modal-close tb-btn tb-btn-text sm low-em"
          data-entity="producer-timeline-closeSettings"
          @click="showSettingsModal = false"
        >
          <span class="fas fa-times" />
        </button>
      </template>

      <div class="producer-settings__field">
          <div class="producer-settings__label">
            Custom Graphic Transition Mode
          </div>

          <div class="producer-settings__description">
            This controls how Custom Graphics should animate in and out when moving between them on the timeline. Out/In waits for the current Custom Graphic to leave before showing the new ones (default). Overlap will start showing the new Custom Graphic right away.
          </div>

          <select
            class="tb-select sm"
            v-model="transitionMode"
            title="Transition mode used when switching between graphics"
            data-entity="producer-timeline-transitionMode"
            @change="setTransitionMode"
          >
            <option value="out-in">Out/In</option>
            <option value="overlap">Overlap</option>
          </select>
        </div>

        <hr />

          <div class="producer-settings__field">
          <div class="producer-settings__label">
            Social Transition Mode
          </div>

          <div class="producer-settings__description">
            This controls how Social should animate in and out when moving between them on the timeline. Cut will immediately hide the current Social Graphic and switch to the new one. Fade will slowly fade in the new Social Graphic over the current one.
          </div>

          <select
            class="tb-select sm"
            v-model="transition"
            title="Transition mode used when switching between Social Graphics"
            data-entity="producer-timeline-transition"
            @change="setTransition"
          >
            <option value="animated cutIn">Cut</option>
            <option value="animated fadeIn">Fade</option>
          </select>
        </div>
    </modal>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { toMinutesAndSeconds } from '../../utils/filters';

import Alert from '../alert/Alert.vue';
import Modal from '../modal/Modal.vue';
import Dropdown from '../../shared/dropdown/Dropdown.vue';

import ConduitEventsService from '../../services/conduitEventsService';
import ProductionsService from '../../services/productions';

import TimelinePanelComponent from './panel/TimelinePanel.vue';
import TimelineGraphicComponent from './graphic/TimelineGraphic.vue';
import TimelineGraphicsComponent from './graphic/TimelineGraphics.vue';
import TimelineSegmentComponent from './segment/TimelineSegment.vue';
import TimelineMultipostComponent from './multipost/TimelineMultipost.vue';
import TimelineThemeComponent from './theme/TimelineTheme.vue';
import TimelineAsset from './TimelineAsset.vue';
import TimelinePoll from './TimelinePoll.vue';
import TimelineMarker from './TimelineMarker.vue';

const dispatchEvent = (event) => {
  const evt = document.createEvent('Event');
  evt.initEvent(event, true, true);
  document.dispatchEvent(evt);
};

export default {
  name: 'Timeline',
  components: {
    Alert,
    Modal,
    Dropdown,

    theme: TimelineThemeComponent,
    graphic: TimelineGraphicComponent,
    graphics: TimelineGraphicsComponent,
    segment: TimelineSegmentComponent,
    panel: TimelinePanelComponent,
    multi: TimelineMultipostComponent,
    asset: TimelineAsset,
    poll: TimelinePoll,
    'timeline-marker': TimelineMarker,
  },

  data() {
    return {
      paused: true,
      interval: null,
      countdown: 0,
      flashTimer: null,
      flashedTimelineId: null,
      showAlert: false,
      showSettingsModal: false,
      showSaveAsShowConfirm: false,
      savingAsShow: false,
      zoom: localStorage.getItem('slider') || 1,
    };
  },

  mounted() {
    if ((this.playing || this.videoPlaying) && this.duration) {
      // Start the timer on the timeline if auto-advancing OR video is playing
      this.startCountdown();
    }
  },

  watch: {
    sub_index(subIndex, oldSubIndex) {
      if (subIndex !== oldSubIndex) {
        this.countdown = 0;

        // Check if the new post is a video, since that can determine if we should keep the timer rolling
        if (this.playing) {
          this.startCountdown();
        } else {
          this.stopCountdown();
        }
      }
    },

    zoom() {
      localStorage.setItem('slider', this.zoom);
    },

    videoPlaying(videoPlaying) {
      if (videoPlaying) {
        this.startCountdown();
      } else {
        this.stopCountdown();
      }
    },

    playing(playing) {
      if (!playing) {
        this.stopCountdown();
      }
    },

    duration(val) {
      if (!val || !this.paused) {
        return;
      }

      // If duration changed, and something is playing but the timer is paused...
      if (this.playing) {
        // ... then start that ish
        this.startCountdown();
      }
    },
  },
  computed: {
    ...mapGetters([
      'getLiveSocialPost',
      'getLiveTagboard',
    ]),

    ...mapState({
      isSocketConnected: (state) => state.clients.isSocketConnected,
    }),

    showDuration() {
      return this.hasActiveItem && this.playing;
    },

    // Used for graphic transitions
    transitionMode: {
      get() {
        return this.production.transitionMode || 'out-in';
      },

      set(transitionMode) {
        this.$set(this.production, 'transitionMode', transitionMode);
      },
    },

    // Used for theme transitions
    transition: {
      get() {
        return this.production.transition || 'animated cutIn';
      },

      set(transition) {
        this.$set(this.production, 'transition', transition);
      },
    },

    production() {
      return this.$store.state.production.production;
    },

    // Root-level timeline items that aren't segments or markers; Shows only
    // allow Segments and Markers at the root, so these would be wrapped.
    unsegmentedItemCount() {
      return (this.production?.timeline || []).filter(
        (item) => item?.layout !== 'segment' && item?.layout !== 'marker',
      ).length;
    },

    saveAsShowNeedsSegments() {
      return this.unsegmentedItemCount > 0;
    },

    saveAsShowCancelLabel() {
      return this.saveAsShowNeedsSegments ? "I'll organize them" : undefined;
    },

    saveAsShowConfirmLabel() {
      return this.saveAsShowNeedsSegments ? 'Segment for this show' : undefined;
    },

    sub_index() {
      return this.production.sub_index;
    },

    layouts: {
      get() {
        return this.production.timeline;
      },
      set(layouts) {
        this.$store.dispatch('setLayouts', layouts);
      },
    },

    videoPlaying() {
      return this.$store.state.production.production.videoPlaying;
    },

    playing() {
      return this.$store.state.production.production.playing;
    },

    timelineMuted() {
      return this.$store.state.production.production.muted;
    },

    timelineLooped() {
      return this.$store.state.production.production.looped;
    },

    hasActiveItem() {
      const { index } = this.production;
      return !!index;
    },

    duration() {
      return this.$store.state.production.duration;
    },
  },

  created() {
    document.addEventListener('scrollTimelineItem', this.onScrollTimelineItem);
  },

  beforeUnmount() {
    document.removeEventListener('scrollTimelineItem', this.onScrollTimelineItem);
    clearTimeout(this.flashTimer);
  },

  methods: {
    toMinutesAndSeconds,

    scrollToTimelineItem(timelineId) {
      if (!timelineId) {
        return;
      }

      this.$nextTick(() => {
        const container = this.$el?.querySelector?.('.playlist-draggable');
        const el = this.$el?.querySelector?.(`[data-timeline-id="${timelineId}"]`);

        if (!container || !el) {
          return;
        }

        const containerRect = container.getBoundingClientRect();
        const elementRect = el.getBoundingClientRect();
        const top = container.scrollTop + (elementRect.top - containerRect.top) - (container.clientHeight / 2) + (elementRect.height / 2);

        container.scrollTo({
          top: Math.max(0, top),
          behavior: 'smooth',
        });
      });
    },

    flashTimelineItem(timelineId) {
      if (!timelineId) {
        return;
      }

      clearTimeout(this.flashTimer);
      this.flashedTimelineId = timelineId;

      this.flashTimer = window.setTimeout(() => {
        if (this.flashedTimelineId === timelineId) {
          this.flashedTimelineId = null;
        }
      }, 1400);
    },

    scrollAndFlashTimelineItem(timelineId) {
      this.scrollToTimelineItem(timelineId);
      this.flashTimelineItem(timelineId);
    },

    onScrollTimelineItem(event) {
      const { timelineId, flash = false } = event?.detail || {};
      this.scrollToTimelineItem(timelineId);

      if (flash) {
        this.flashTimelineItem(timelineId);
      }
    },

    async addGraphicsGroup() {
      await this.$store.dispatch('addGraphicsGroup');
      await this.$nextTick();
      this.scrollAndFlashTimelineItem(this.layouts?.[this.layouts.length - 1]?.timeline_id);

      ConduitEventsService.track(
          'producer.created.group-from-playlist-button',
          { production_id: this.production._id },
          {
            ownerId: this.production.owner,
            userId: this.$store.state.user.id,
            isStaff: this.$store.getters.isStaff,
           },
      );
    },

    async addSegment() {
      await this.$store.dispatch('addSegment');
      await this.$nextTick();
      this.scrollAndFlashTimelineItem(this.layouts?.[this.layouts.length - 1]?.timeline_id);

      ConduitEventsService.track(
        'producer.created.segment-from-playlist-button',
        { production_id: this.production._id },
        {
          ownerId: this.production.owner,
          userId: this.$store.state.user.id,
          isStaff: this.$store.getters.isStaff,
        },
      );
    },

    async addMarker() {
      await this.$store.dispatch('addMarker');
      await this.$nextTick();
      this.scrollAndFlashTimelineItem(this.layouts?.[this.layouts.length - 1]?.timeline_id);

      ConduitEventsService.track(
        'producer.created.marker-from-playlist-button',
        { production_id: this.production._id },
        {
          ownerId: this.production.owner,
          userId: this.$store.state.user.id,
          isStaff: this.$store.getters.isStaff,
        },
      );
    },

    confirmSaveAsShow() {
      this.showSaveAsShowConfirm = true;
    },

    async saveAsShow() {
      if (!this.production?._id || this.savingAsShow) {
        return;
      }

      this.showSaveAsShowConfirm = false;
      this.savingAsShow = true;

      try {
        const { showId } = await ProductionsService.generateShow(this.production._id);

        const appUrl = process.env.TAGBOARD_APP_URL || 'https://app.tagboard.com';

        this.$store.commit('ADD_OUTPUT_TOAST', {
          title: 'Show created',
          message: `"${this.production.title || 'Untitled production'}" has been saved as a Show.`,
          link: `${appUrl}/show/${showId}`,
          linkLabel: 'Open in Show Editor',
          type: 'success',
          hideAfter: 5000,
        });

        // Let the Shows tab know a new Show exists so it can refresh its list.
        dispatchEvent('showsChanged');
      } catch (err) {
        this.$store.commit('ADD_OUTPUT_TOAST', {
          title: 'Could not create Show',
          message: err?.message || 'Something went wrong while saving this production as a Show.',
          type: 'error',
          hideAfter: 5000,
        });
      } finally {
        this.savingAsShow = false;
      }
    },

    startCountdown() {
      this.paused = false;

      clearInterval(this.interval);

      let prevTime = Date.now();

      this.interval = window.setInterval(() => {
        if (this.paused) { return; }

        // Get exact amount of time that has passed,
        // jic the interval has been throttled by the browser
        this.countdown += Date.now() - prevTime;
        prevTime = Date.now();

        if (this.countdown >= this.duration) {
          // Reset to zero, but keep the countdown running.
          // That way it'll loop or keep going for the next post.
          this.countdown = 0;
        }
      }, 100);
    },

    stopCountdown() {
      this.paused = true;
    },

    setTransitionMode(e) {
      this.$store.dispatch('sendSettings');
      this.$store.dispatch('sendPreviewSettings');

      ConduitEventsService.track(
          'producer.changed.transition-mode',
          {
            transitionMode: e.target.value,
            ownerId: this.production.owner,
            production_id: this.production._id,
          },
          {
            ownerId: this.production.owner,
            userId: this.$store.state.user.id,
            isStaff: this.$store.getters.isStaff,
          },
      );
    },

    setTransition(e) {
      this.$store.dispatch('sendSettings');
      this.$store.dispatch('sendPreviewSettings');

      ConduitEventsService.track(
          'producer.changed.transition',
          {
            transition: e.target.value,
            ownerId: this.production.owner,
            production_id: this.production._id,
          },
          {
            ownerId: this.production.owner,
            userId: this.$store.state.user.id,
            isStaff: this.$store.getters.isStaff,
          },
      );
    },

    togglePlaying() {
      this.$store.dispatch(this.playing ? 'pauseTimeline' : 'playTimeline');
    },

    toggleMute() {
      this.$store.dispatch('toggleMute');
    },

    toggleLoop() {
      this.$store.dispatch('toggleLoop');
    },

    confirm() {
      this.showAlert = true;
    },

    clearTimeline() {
      this.$store.dispatch('clearLayerEditing');
      this.$store.dispatch('clearTimeline');
      this.$store.dispatch('clearPreview');
      this.showAlert = false;
      this.cleanupStrayNodes();
    },

    startDrag(e) {
      // Since the timeline is filled with different components,
      // we need to check the actual type set on the element here
      const type = e.item.dataset.componentType;
      this.$store.dispatch('setDragType', type || 'component');
    },

    endDrag() {
      this.$store.dispatch('clearDragType');
      this.cleanupStrayNodes();
    },

    /**
     * Remove any DOM nodes Sortable left inside the timeline draggable that no
     * longer correspond to a timeline item. Nested-list drags (root <-> segment)
     * can leave a stray moved node behind after a reverted/desynced drop, and
     * because Vue doesn't manage those nodes it never removes them — so they
     * survive operations like Clear and render as a broken item.
     */
    cleanupStrayNodes() {
      this.$nextTick(() => {
        const container = this.$el?.querySelector('.playlist-draggable');
        if (!container) {
          return;
        }

        const validIds = new Set((this.layouts || []).map((item) => item?.timeline_id));

        Array.from(container.children).forEach((child) => {
          const timelineId = child.getAttribute?.('data-timeline-id');
          if (timelineId && !validIds.has(timelineId)) {
            child.remove();
          }
        });
      });
    },

    async onChange(e) {
      // Type not added, so don't log any events
      if (!e.added) {
        return;
      }

      // This should be handled with global state,
      // but we'll just let the tabs know to clear for now
      dispatchEvent('clearTabSelection');

      const { element, newIndex } = e.added;

      const { layout, type } = element;

      const { group, pendingGroup } = this.$store.state.drag;

      // A whole Show may have been dropped while its full contents were still
      // loading from the API (see ShowList startShowDrag). In that case the
      // drag group isn't populated yet, so await the pending load and insert
      // the real items instead of leaving the placeholder segment behind.
      let items = group && group.length ? group : null;

      if (!items && pendingGroup) {
        try {
          items = await pendingGroup;
        } catch (err) {
          items = null;
        }
      }

      ConduitEventsService.track(
        'producer.added.items-to-playlist',
        {
          type,
          count: items?.length || 1,
          production_id: this.production._id,
          component: 'Timeline.vue',
          method: 'onChange',
          items,
        },
        {
          ownerId: this.production.owner,
          userId: this.$store.state.user.id,
          isStaff: this.$store.getters.isStaff,
        },
      );

      if (items && items.length) {
        // Add entire group to playlist
        this.$store.dispatch('replacePlaylistItem', {
          index: newIndex,
          items,
        });

        // Ensure all metadata is loaded in lookup tables,
        // and also make sure any QR code URLs are shortened.
        await this.$store.dispatch('getTimelineItems', {
          timelineIds: items.map((item) => item.timeline_id),
        });

        this.$store.dispatch('updateTimeline');
        this.$store.dispatch('trackSportradarInGameElement', items.map((item) => item.timeline_id));
        document.dispatchEvent(new CustomEvent('scrollTimelineItem', {
          detail: { timelineId: items[0]?.timeline_id, flash: true },
        }));

        return;
      }

      if (pendingGroup) {
        // The Show's contents failed to load, so remove the placeholder
        // segment instead of leaving an empty Show on the timeline.
        this.$store.dispatch('removeTimelineItem', { timelineId: element.timeline_id });
        return;
      }

      // Single-item drag: deterministically place the dragged element at the
      // root exactly once, and not inside any container (e.g. when dragging
      // it out of a segment, nested-list drags can leave it duplicated). This
      // runs before any async work so a Clear (or other change) can't race
      // with the placement and leave the item duplicated or re-inserted into
      // an emptied timeline.
      this.$store.dispatch('moveTimelineItemsToContainer', {
        items: [element],
        containerTimelineId: null,
        index: newIndex,
      });

      if (layout !== 'marker') {
        await this.$store.dispatch('getTimelineItems', {
          timelineIds: [element.timeline_id],
        });
      }

      this.$store.dispatch('updateTimeline');
      this.$store.dispatch('trackSportradarInGameElement', [element.timeline_id]);
      document.dispatchEvent(new CustomEvent('scrollTimelineItem', {
        detail: { timelineId: element.timeline_id, flash: true },
      }));
    },

    advance(direction) {
      this.$store.dispatch('advance', direction);
    },

    getType(item) {
      const { layout } = item;

      if (['panel', 'graphic', 'graphics', 'segment', 'asset', 'poll'].includes(layout)) {
        // Just return the layout directly
        return item.layout;
      }

      if (layout === 'marker') {
        return 'timeline-marker';
      }

      // Timeline item is multipost if it's a specific type of theme
      // or if it contains a tagboard as it's first post
      const isMultiPostLayout = (
        ['grid', 'socialburst', 'waterfall', 'carousel', 'mural', 'upnext'].includes(layout)
        || (item.posts?.[0]?.type === 'tagboard')
      );

      // If it's not a multipost element, then we'll call it a theme
      return isMultiPostLayout ? 'multi' : 'theme';
    },
  },
};
</script>
